// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Dialog, DialogActions, DialogContent, Typography, Button } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import stylesDef from './styles';

interface PlanHistoryDialogProps {
  showDialog: boolean;
  planHistory: IOrgPastPlan[];
  onCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

// a subscription period may not know a date or a figure; the row still renders
const dateText = (value: Date | string | null) => (value ? moment(value).format('LL') : '—');
const creditsText = (value: number | null) => (typeof value === 'number' ? value : '—');

/**
 * Every plan period the account has had, whatever its origin (AUT-6571): the server names each row — a plan by its
 * category (with the Credit Ladder tier a migrated row prices at), a Stripe subscription period by its billing
 * interval — so the dialog shows the same card for both.
 */
export const PlanHistoryDialog = ({ showDialog, planHistory, onCancel }: PlanHistoryDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  return (
    <Dialog open={showDialog} onClose={onCancel} sx={styles.dialog} maxWidth="md">
      <Box sx={styles.dialogBox}>
        <Typography variant="h3" sx={styles.titleText}>Plan History</Typography>
        <DialogContent sx={styles.dialogContent}>
          {planHistory?.map((plan) => (
            <Box sx={styles.planCard} key={`${plan.planName || plan.name}-${plan.startDate ? String(plan.startDate) : 'undated'}-${plan.creditsPurchased}`}>
              <Box>
                <Typography sx={styles.planName}>{plan.name || 'Plan'}</Typography>
                <Box sx={styles.planDate}>
                  <AccessTimeIcon sx={styles.timeIcon} />
                  <Typography sx={styles.planDateText}>From: </Typography>
                  <Typography component="span">{dateText(plan.startDate)}</Typography>
                </Box>
                <Box sx={styles.planDate}>
                  <AccessTimeIcon sx={styles.timeIcon} />
                  <Typography sx={styles.planDateText}>Until: </Typography>
                  <Typography component="span">{dateText(plan.endDate)}</Typography>
                </Box>
              </Box>
              <Box sx={styles.creditsInfo}>
                <Box sx={styles.creditsInfoText}>
                  <Typography variant="h4" sx={styles.credit}>{creditsText(plan.creditsPurchased)}</Typography>
                  <Typography variant="h6" sx={styles.text}>credits</Typography>
                  <Typography variant="h6" sx={styles.text}>purchased</Typography>
                </Box>
                <Box sx={styles.line} />
                <Box sx={styles.creditsInfoText}>
                  <Typography variant="h4" sx={styles.credit}>{creditsText(plan.usedCredits)}</Typography>
                  <Typography variant="h6" sx={styles.text}>credits</Typography>
                  <Typography variant="h6" sx={styles.text}>used</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} sx={styles.okButton}>Close</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PlanHistoryDialog;
